import type { RouteLocationNormalized } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'
import AppLayout from '@/layouts/AppLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import FullscreenLayout from '@/layouts/FullscreenLayout.vue'
import type { PermissionAction, PermissionResource } from '@/lib/permissions'
import { useAuthStore } from '@/stores/auth'
import ForgotPasswordView from '@/views/authentication/ForgotPasswordView.vue'
import LoginView from '@/views/authentication/LoginView.vue'
import RegisterView from '@/views/authentication/RegisterView.vue'
import VerifyEmailView from '@/views/authentication/VerifyEmailView.vue'
import Billing from '@/views/Billing.vue'
import Bookings from '@/views/Bookings.vue'
import Dashboard from '@/views/Dashboard.vue'
import Finances from '@/views/Finances.vue'
import Offline from '@/views/flatpages/Offline.vue'
import Guests from '@/views/Guests.vue'
import Properties from '@/views/Properties.vue'
import PropertyDetails from '@/views/properties/PropertyDetails.vue'
import ScreenDetails from '@/views/screens/ScreenDetails.vue'
import ScreenDisplay from '@/views/screens/ScreenDisplay.vue'
import Screens from '@/views/screens/ScreensView.vue'

type PermissionMeta = {
  resource: PermissionResource
  action: PermissionAction
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // App layout routes (with sidebar, header, etc.)
    {
      path: '/',
      component: AppLayout,
      children: [
        {
          path: '',
          name: 'Dashboard',
          component: Dashboard,
          meta: { title: 'Dashboard' },
        },
        {
          path: '/finances',
          name: 'Finances',
          component: Finances,
          meta: { title: 'Finances' },
        },
        {
          path: '/billing',
          name: 'Billing',
          component: Billing,
          meta: { title: 'Billing' },
        },
        {
          path: '/billing/history',
          name: 'BillingHistory',
          component: () => import('@/views/billing/PaymentHistory.vue'),
          meta: { title: 'Payment History' },
        },
        {
          path: '/screens',
          name: 'Screens',
          component: Screens,
          meta: {
            title: 'TV Screens',
            permission: { resource: 'screens', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/screens/:id',
          name: 'ScreenDetails',
          component: ScreenDetails,
          meta: {
            title: 'Screen Details',
            permission: { resource: 'screens', action: 'view' } as PermissionMeta,
          },
          props: true,
        },
        {
          path: '/properties',
          name: 'Properties',
          component: Properties,
          meta: {
            title: 'Properties',
            permission: { resource: 'properties', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/properties/:id',
          name: 'PropertyDetails',
          component: PropertyDetails,
          meta: {
            title: 'Property Details',
            permission: { resource: 'properties', action: 'view' } as PermissionMeta,
          },
          props: true,
        },
        {
          path: '/bookings',
          name: 'Bookings',
          component: Bookings,
          meta: {
            title: 'Bookings',
            permission: { resource: 'bookings', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/guests',
          name: 'Guests',
          component: Guests,
          meta: {
            title: 'Guests',
            permission: { resource: 'guests', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/guests/:id',
          name: 'GuestDetail',
          component: () => import('@/views/guests/GuestDetail.vue'),
          meta: {
            title: 'Guest Details',
            permission: { resource: 'guests', action: 'view' } as PermissionMeta,
          },
          props: true,
        },
        {
          path: '/settings/company',
          name: 'SettingsCompany',
          component: () => import('@/views/settings/CompanySettings.vue'),
          meta: {
            title: 'Company Settings',
            permission: { resource: 'companies', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/settings/company/:id',
          name: 'SettingsCompanyDetail',
          component: () => import('@/views/settings/CompanyDetail.vue'),
          meta: {
            title: 'Company Settings',
            permission: { resource: 'companies', action: 'view' } as PermissionMeta,
          },
          props: true,
        },
        {
          path: '/dev',
          name: 'Developer',
          component: () => import('@/views/dev/DeveloperView.vue'),
          meta: { title: 'Developer Tools' },
        },
        {
          path: '/profile',
          name: 'Profile',
          component: () => import('@/views/Profile.vue'),
          meta: { title: 'Profile' },
        },
        {
          path: '/calendar-sync',
          name: 'CalendarSync',
          component: () => import('@/views/addons/CalendarSync.vue'),
          meta: {
            title: 'Calendar Sync',
            featureName: 'Calendar Sync',
            description:
              'Sync your bookings across platforms. Import Airbnb and Booking.com bookings via iCal into your CasaMana calendar.',
            features: [
              'Airbnb iCal import',
              'Booking.com iCal import',
              'VRBO calendar sync',
              'Google Calendar integration',
              'Cross-platform conflict detection',
            ],
          },
        },
        {
          path: '/landing-page',
          name: 'LandingPage',
          component: () => import('@/views/flatpages/ComingSoon.vue'),
          meta: {
            title: 'Landing Page',
            featureName: 'Landing Page',
            description:
              'Create a stunning public landing page for your property with built-in booking capabilities — no coding required.',
            features: [
              'Customizable property showcase',
              'Built-in booking widget',
              'Guest reviews and testimonials',
              'Photo gallery with lightbox',
              'SEO optimized',
              'Mobile responsive design',
            ],
          },
        },
        {
          path: '/fb-conversions',
          name: 'FBConversions',
          component: () => import('@/views/flatpages/ComingSoon.vue'),
          meta: {
            title: 'FB Conversions API',
            featureName: 'Facebook Conversions API',
            description:
              'Track Facebook ad performance — see exactly which ads bring bookings and measure your return on ad spend.',
            features: [
              'Facebook Pixel integration',
              'Automatic conversion events on bookings',
              'ROAS measurement dashboard',
              'Ad attribution tracking',
              'Custom audience building from guests',
            ],
          },
        },
        {
          path: '/fb-messenger-bot',
          name: 'FBMessengerBot',
          component: () => import('@/views/flatpages/ComingSoon.vue'),
          meta: {
            title: 'FB Messenger Bot',
            featureName: 'Facebook Messenger Bot',
            description:
              'Auto-reply bot for your Facebook Page — answers availability, pricing, and books guests automatically.',
            features: [
              'Auto-reply to availability inquiries',
              'Create bookings directly from chat',
              'Pricing and rate responses',
              'Escalation to property owner',
              'Facebook Page integration',
            ],
          },
        },
        {
          path: '/inventory',
          name: 'Inventory',
          component: () => import('@/views/flatpages/ComingSoon.vue'),
          meta: {
            title: 'Inventory',
            featureName: 'Inventory',
            description:
              'Track consumables, cleaning supplies, maintenance parts, and manage purchasing and stock levels across your properties.',
            features: [
              'Consumables tracking (soap, shampoo, water, coffee)',
              'Cleaning supplies management',
              'Maintenance parts inventory',
              'Purchasing and stock levels',
              'Room-level assignment and issuance',
              'Low-stock alerts and notifications',
              'Full audit trail',
            ],
          },
        },
        {
          path: '/requests',
          name: 'Feedback',
          component: () => import('@/views/maintenance/Requests.vue'),
          meta: { title: 'Feedback' },
        },
        {
          path: '/requests/:id',
          name: 'FeatureRequestDetail',
          component: () => import('@/views/maintenance/RequestDetail.vue'),
          meta: { title: 'Request Details' },
          props: true,
        },
        {
          path: '/help',
          name: 'Help',
          component: () => import('@/views/Help.vue'),
          meta: { title: 'Help' },
        },
        {
          path: '/about',
          name: 'About',
          component: () => import('@/views/About.vue'),
          meta: { title: 'About' },
        },
        {
          path: '/admin/users',
          name: 'AdminUsers',
          component: () => import('@/views/admin/Users.vue'),
          meta: {
            title: 'Users',
            permission: { resource: 'users', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/admin/users/pending',
          name: 'AdminUsersPending',
          component: () => import('@/views/admin/PendingUsers.vue'),
          meta: {
            title: 'Pending Users',
            permission: { resource: 'users', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/admin/users/banned',
          name: 'AdminUsersBanned',
          component: () => import('@/views/admin/BannedUsers.vue'),
          meta: {
            title: 'Banned Users',
            permission: { resource: 'users', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/admin/companies',
          name: 'AdminCompanies',
          component: () => import('@/views/admin/Companies.vue'),
          meta: {
            title: 'Companies',
            permission: { resource: 'companies', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/admin/requests',
          name: 'AdminRequests',
          component: () => import('@/views/admin/Requests.vue'),
          meta: {
            title: 'Admin Requests',
            permission: { resource: 'requests', action: 'edit' } as PermissionMeta,
          },
        },
        {
          path: '/admin/announcements',
          name: 'AdminAnnouncements',
          component: () => import('@/views/admin/Announcements.vue'),
          meta: {
            title: 'Announcements',
            permission: { resource: 'announcements', action: 'edit' } as PermissionMeta,
          },
        },
        {
          path: '/settings/notifications',
          name: 'SettingsNotifications',
          component: () => import('@/views/settings/Notifications.vue'),
          meta: { title: 'Notification Settings' },
        },
        {
          path: '/settings/announcements',
          name: 'SettingsAnnouncements',
          component: () => import('@/views/settings/Announcements.vue'),
          meta: {
            title: 'Announcements',
            permission: { resource: 'announcements', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/admin/billing',
          name: 'AdminBilling',
          component: () => import('@/views/admin/BillingAdmin.vue'),
          meta: {
            title: 'Billing',
            permission: { resource: 'billing', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/admin/vouchers',
          name: 'AdminVouchers',
          component: () => import('@/views/admin/Vouchers.vue'),
          meta: {
            title: 'Vouchers',
            permission: { resource: 'billing', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/admin/contacts',
          name: 'AdminContacts',
          component: () => import('@/views/admin/Contacts.vue'),
          meta: {
            title: 'Contacts',
            permission: { resource: 'contacts', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/admin/email-settings',
          name: 'AdminEmailSettings',
          component: () => import('@/views/admin/EmailSettings.vue'),
          meta: {
            title: 'Email Settings',
            permission: { resource: 'email_settings', action: 'view' } as PermissionMeta,
          },
        },
        {
          path: '/pending-approval',
          name: 'PendingApproval',
          component: () => import('@/views/flatpages/PendingApproval.vue'),
          meta: { title: 'Pending Approval', bypassApproval: true },
        },
        {
          path: '/offline',
          name: 'Offline',
          component: Offline,
          meta: { title: 'Offline' },
        },
        {
          path: '/forbidden',
          name: 'Forbidden',
          component: () => import('@/views/flatpages/Forbidden.vue'),
          meta: { title: 'Forbidden' },
        },
      ],
    },

    // Authentication routes (no sidebar/navbar, but with footer)
    {
      path: '/',
      component: AuthLayout,
      children: [
        {
          path: '/login',
          name: 'Login',
          component: LoginView,
          meta: { title: 'Login' },
        },
        {
          path: '/register',
          name: 'Register',
          component: RegisterView,
          meta: { title: 'Register' },
        },
        {
          path: '/forgot-password',
          name: 'ForgotPassword',
          component: ForgotPasswordView,
          meta: { title: 'Forgot Password' },
        },
        {
          path: '/verify-email',
          name: 'VerifyEmail',
          component: VerifyEmailView,
          meta: { title: 'Verify Email' },
        },
      ],
    },

    // Fullscreen layout route for ScreenViewer (no sidebar, header, etc.)
    {
      path: '/screens/:id/view',
      component: FullscreenLayout,
      children: [
        {
          path: '',
          name: 'ScreenViewer',
          component: ScreenDisplay,
          meta: { title: 'Screen Viewer' },
          props: true,
        },
      ],
    },

    // 404 catch-all route - must be the last one
    {
      path: '/:pathMatch(.*)*',
      component: AppLayout,
      children: [
        {
          path: '',
          name: 'NotFound',
          component: () => import('@/views/flatpages/NotFound.vue'),
          meta: { title: 'Not Found' },
        },
      ],
    },
  ],
})

router.beforeEach(async (to: RouteLocationNormalized) => {
  document.title = to.meta.title ? `${to.meta.title} | CasaMana` : 'CasaMana'

  const auth = useAuthStore()
  const isAuthenticated = auth.isAuthenticated
  const isAuthRoute = ['/login', '/register', '/forgot-password', '/verify-email'].includes(to.path)

  if (!isAuthenticated && !isAuthRoute) return '/login'
  if (isAuthenticated && isAuthRoute) return '/'

  if (isAuthenticated && !auth.isApproved && auth.role !== 'user' && !to.meta.bypassApproval) {
    return '/pending-approval'
  }

  if (auth.initializing) return

  const permission = to.meta.permission as PermissionMeta | undefined
  if (permission && !auth.can(permission.resource, permission.action)) {
    return '/forbidden'
  }
})

router.afterEach((to) => {
  import('@/services/eventLog').then(({ trackPageView }) => {
    trackPageView(useAuthStore().user?.id, to.path)
  })
})

export default router
